// ============================================================
// secureInvitation — recipient-side data + state model for the client portal.
//
// The recipient opens a signed secure link. This module maps the server's
// recipient-safe responses (resolve → redeem → verify-session → manifest)
// onto the portal's view state machine. The browser never holds a token,
// code hash, or session secret — the verified session lives in an httpOnly
// cookie and every view is driven by server truth.
//
// AUTHORITY=B: the external engine remains the invitation authority; Square
// Code is the control plane. Branding rule (owner decision, End User Portal
// Activation): the visible product is SQUARE CODE (or the configured client
// brand) — internal historical identifiers are NEVER shown to a recipient.
// ============================================================

import { api, type RecipientManifest } from '../../../api/client'

// The recipient portal view state machine. `preparing` is the SSR/initial
// state. ready/expires_soon come from a non-consuming resolve; redeeming/
// code_required belong to redemption; nda/notification_preferences/
// portal_home are REAL, server-persisted steps behind a verified session.
export type RecipientView =
  | 'preparing'
  | 'ready'
  | 'expires_soon'
  | 'code_required'
  | 'redeeming'
  | 'nda'
  | 'notification_preferences'
  | 'portal_home'
  | 'provider_required'
  | 'unverifiable'
  | 'expired'
  | 'revoked'
  | 'denied'
  | 'not_live'
  | 'link_failed'

export interface SecureInvitationDisplay {
  projectName: string | null
  inviterName: string | null
  purpose: string | null
  expiresAt: string | null
  ndaRequired: boolean
  notificationPreferencesRequired: boolean
  accountMode: string | null
  fallbackAvailable: boolean
  correlationId: string | null
}

export interface ResolveOutcome {
  view: RecipientView
  display: SecureInvitationDisplay
  message: string
}

// Strings that must NEVER appear in recipient-facing output. Owner decision:
// the recipient-visible product name is Square Code (or the client brand);
// internal/trust-layer identifiers stay internal. Exported so a regression
// test can scan the rendered portal for leak terms.
export const FORBIDDEN_RECIPIENT_TERMS = [
  'Olivium',
  'olivium',
  'olv_',
  'Command Center',
  'CommandCenter',
  'command-center',
  'service_role',
  'service role',
  'olivium_secure_shares',
  'code_hash',
  'bridge',
]

// Map the resolve `status` onto a recipient view. Unknown → link_failed (never a
// success). `ready`/`expires_soon` are the only success entries.
export function viewFromStatus(status: string | undefined): RecipientView {
  switch (status) {
    case 'ready': return 'ready'
    case 'expires_soon': return 'expires_soon'
    case 'provider_required': return 'provider_required'
    case 'unverifiable': return 'unverifiable'
    case 'expired': return 'expired'
    case 'revoked': return 'revoked'
    case 'denied': return 'denied'
    case 'not_live': return 'not_live'
    case 'error': return 'link_failed'
    default: return 'link_failed'
  }
}

// Map a redeem/verify-session denial `state` onto a recipient view. Terminal
// states terminate; a generic codeless denial asks for the fallback code.
export function viewFromMemberState(state: string | undefined): RecipientView {
  switch (state) {
    case 'code_required': return 'code_required'
    case 'expired': return 'expired'
    case 'revoked': return 'revoked'
    case 'already_used': return 'denied'
    case 'not_live': return 'not_live'
    case 'no_session': return 'ready'
    case 'denied': return 'denied'
    default: return 'link_failed'
  }
}

/** Route a verified session into the right onboarding/portal view from
 *  MANIFEST server truth — a refresh always lands exactly here. */
export function viewFromManifest(manifest: RecipientManifest): RecipientView {
  if (manifest.onboarding.nda.required && !manifest.onboarding.nda.accepted) return 'nda'
  if (!manifest.onboarding.preferences.set) return 'notification_preferences'
  return 'portal_home'
}

const EMPTY_DISPLAY: SecureInvitationDisplay = {
  projectName: null, inviterName: null, purpose: null, expiresAt: null,
  ndaRequired: false, notificationPreferencesRequired: false,
  accountMode: null, fallbackAvailable: false, correlationId: null,
}

// Call the non-consuming resolve proxy and produce a view outcome. Fails safe:
// any thrown/transport error maps to a retryable link_failed, never a fake ready.
export async function resolveSecureInvitation(ref: { token?: string; invite_id?: string }): Promise<ResolveOutcome> {
  try {
    const r = await api.access.resolve(ref)
    return {
      view: viewFromStatus(r.status),
      message: r.message,
      display: {
        projectName: r.display_project_name ?? null,
        inviterName: r.display_inviter_name ?? null,
        purpose: r.display_purpose ?? null,
        expiresAt: r.expires_at ?? null,
        ndaRequired: r.nda_required === true,
        notificationPreferencesRequired: r.notification_preferences_required === true,
        accountMode: r.account_mode ?? null,
        fallbackAvailable: r.fallback_available === true,
        correlationId: r.correlation_id ?? null,
      },
    }
  } catch {
    return { view: 'link_failed', message: RECIPIENT_COPY.link_failed.body, display: EMPTY_DISPLAY }
  }
}

// ── Presentation contract per view ──
// Every state carries an icon glyph + label + explanation + action semantics, so
// status is never communicated by color alone (accessibility requirement).

export type RecipientTone = 'progress' | 'ready' | 'attention' | 'blocked' | 'neutral'

export interface RecipientCopy {
  tone: RecipientTone
  icon: string      // decorative glyph (aria-hidden); the label carries meaning
  label: string     // short status label (screen-reader safe)
  title: string
  body: string
}

export const RECIPIENT_COPY: Record<RecipientView, RecipientCopy> = {
  preparing: {
    tone: 'progress', icon: '◌', label: 'Preparing',
    title: 'Preparing your secure workspace',
    body: 'One moment while we confirm your secure invitation.',
  },
  ready: {
    tone: 'ready', icon: '✓', label: 'Ready',
    title: 'Your secure workspace is ready',
    body: 'Your invitation is confirmed. Continue to open your access.',
  },
  expires_soon: {
    tone: 'attention', icon: '!', label: 'Expires soon',
    title: 'Your secure workspace is ready',
    body: 'Your invitation is confirmed, but it expires soon. Continue now to open your access.',
  },
  code_required: {
    tone: 'attention', icon: '#', label: 'Code required',
    title: 'Enter your access code',
    body: 'This workspace asks for the one-time access code you were given. Enter it below to continue.',
  },
  redeeming: {
    tone: 'progress', icon: '◌', label: 'Opening',
    title: 'Opening your secure workspace',
    body: 'Verifying your access…',
  },
  nda: {
    tone: 'attention', icon: '§', label: 'Agreement required',
    title: 'Review the confidentiality agreement',
    body: 'Before you enter, this workspace asks you to accept a confidentiality agreement. Your acceptance is recorded.',
  },
  notification_preferences: {
    tone: 'attention', icon: '⌂', label: 'Preferences',
    title: 'Choose how you’d like to be notified',
    body: 'Pick how you want to hear about updates to this workspace. Your choices are saved and you can change them later.',
  },
  portal_home: {
    tone: 'ready', icon: '✓', label: 'Workspace',
    title: 'Your workspace',
    body: 'You have secure guest access to this project.',
  },
  provider_required: {
    tone: 'neutral', icon: '◔', label: 'Not available yet',
    title: 'This secure workspace isn’t available yet',
    body: 'Your invitation has not been used. Please try again in a little while, or contact the person who invited you.',
  },
  unverifiable: {
    tone: 'attention', icon: '?', label: 'Couldn’t open link',
    title: 'We couldn’t open this secure link',
    body: 'The link may be incomplete. You can try again, or enter an access code instead.',
  },
  expired: {
    tone: 'blocked', icon: '⏳', label: 'Expired',
    title: 'This invitation has expired',
    body: 'For your security this secure invitation is no longer valid. Please ask the person who invited you for a new one.',
  },
  revoked: {
    tone: 'blocked', icon: '⦸', label: 'Revoked',
    title: 'This access has been revoked',
    body: 'Access through this invitation has been turned off. Please contact the person who invited you.',
  },
  denied: {
    tone: 'blocked', icon: '×', label: 'Can’t be opened',
    title: 'This invitation can’t open this workspace',
    body: 'This link isn’t able to open the workspace it points to. Please contact the person who invited you.',
  },
  not_live: {
    tone: 'neutral', icon: '◔', label: 'Not open yet',
    title: 'This secure workspace isn’t open yet',
    body: 'Secure access hasn’t been switched on yet. Please try again later.',
  },
  link_failed: {
    tone: 'attention', icon: '?', label: 'Something went wrong',
    title: 'Something went wrong opening your link',
    body: 'We couldn’t reach the secure service just now. Please try again in a moment.',
  },
}

// Views that offer a "Try again" retry (transient / recoverable).
export const RETRYABLE_VIEWS: RecipientView[] = ['provider_required', 'unverifiable', 'not_live', 'link_failed']
// Views from which the recipient may reveal the optional access-code fallback.
export const FALLBACK_VIEWS: RecipientView[] = ['unverifiable', 'link_failed', 'ready', 'expires_soon']